import { useState, useEffect, useCallback, useRef } from 'react'
import api from '../lib/api'

const EMPTY_STATS = {
  emailSync: { active: 0, completed: 0, failed: 0, activeJobs: [] },
  contactProcessing: { totalEmails: 0, processedEmails: 0, unprocessedEmails: 0 },
  sentimentAnalysis: { totalContacts: 0, analyzedContacts: 0, unanalyzedContacts: 0 },
}

function statusDotClass(isLive) {
  return isLive ? 'status-dot status-dot-running' : 'status-dot status-dot-idle'
}

function pct(num, total) {
  if (!total) return 0
  return Math.min(100, Math.max(0, Math.round((num / total) * 100)))
}

export function LiveJobsDashboard({ socket }) {
  const [stats, setStats] = useState(EMPTY_STATS)
  const [contactLive, setContactLive] = useState(null)
  const [sentimentLive, setSentimentLive] = useState(null)
  const [syncLive, setSyncLive] = useState({})
  const [cancellingSync, setCancellingSync] = useState({})
  const [cancellingContact, setCancellingContact] = useState(false)
  const [cancellingSentiment, setCancellingSentiment] = useState(false)
  const fetchRef = useRef(null)

  const cancelSyncJob = useCallback(async (jobId) => {
    if (!jobId || cancellingSync[jobId]) return
    setCancellingSync((prev) => ({ ...prev, [jobId]: true }))
    try {
      await api.post(`/sync-jobs/${jobId}/cancel`)
    } catch (err) {
      console.error('Cancel sync failed:', err?.response?.data?.error || err.message)
    } finally {
      setCancellingSync((prev) => { const n = { ...prev }; delete n[jobId]; return n })
      fetchRef.current?.()
    }
  }, [cancellingSync])

  const cancelContact = useCallback(async () => {
    if (cancellingContact) return
    setCancellingContact(true)
    try {
      await api.post('/contacts/process/cancel')
    } catch (err) {
      console.error('Cancel contact processing failed:', err?.response?.data?.error || err.message)
    } finally {
      setCancellingContact(false)
      fetchRef.current?.()
    }
  }, [cancellingContact])

  const cancelSentiment = useCallback(async () => {
    if (cancellingSentiment) return
    setCancellingSentiment(true)
    try {
      await api.post('/contacts/sentiments/process/cancel')
    } catch (err) {
      console.error('Cancel sentiment processing failed:', err?.response?.data?.error || err.message)
    } finally {
      setCancellingSentiment(false)
      fetchRef.current?.()
    }
  }, [cancellingSentiment])

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/jobs/stats')
      setStats(res.data)
    } catch {
      // non-critical — keep stale data
    }
  }, [])

  useEffect(() => { fetchRef.current = fetchStats }, [fetchStats])

  useEffect(() => {
    fetchStats()
    const id = setInterval(fetchStats, 5000)
    return () => clearInterval(id)
  }, [fetchStats])

  useEffect(() => {
    if (!socket) return

    // Track only the authoritative totalRemaining from the worker, not an accumulator.
    // The worker computes remaining via a DB count after each batch — that's the truth.
    const onContactProgress = (data) => {
      setContactLive({ totalRemaining: data.totalRemaining, lastBatch: data })
    }
    const onContactDone = () => { setContactLive(null); fetchRef.current?.() }

    const onSentimentProgress = (data) => {
      setSentimentLive({ totalRemaining: data.totalRemaining, lastBatch: data })
    }
    const onSentimentDone = () => { setSentimentLive(null); fetchRef.current?.() }

    const onSyncProgress = (data) => {
      setSyncLive((prev) => ({ ...prev, [data.jobId]: data }))
    }
    const onSyncDone = (data) => {
      setSyncLive((prev) => { const n = { ...prev }; delete n[data.jobId]; return n })
      fetchRef.current?.()
    }

    socket.on('contact-job-progress', onContactProgress)
    socket.on('contact-job-complete', onContactDone)
    socket.on('contact-job-failed', onContactDone)
    socket.on('sentiment-job-progress', onSentimentProgress)
    socket.on('sentiment-job-complete', onSentimentDone)
    socket.on('sentiment-job-failed', onSentimentDone)
    socket.on('job-progress', onSyncProgress)
    socket.on('job-complete', onSyncDone)
    socket.on('job-failed', onSyncDone)

    return () => {
      socket.off('contact-job-progress', onContactProgress)
      socket.off('contact-job-complete', onContactDone)
      socket.off('contact-job-failed', onContactDone)
      socket.off('sentiment-job-progress', onSentimentProgress)
      socket.off('sentiment-job-complete', onSentimentDone)
      socket.off('sentiment-job-failed', onSentimentDone)
      socket.off('job-progress', onSyncProgress)
      socket.off('job-complete', onSyncDone)
      socket.off('job-failed', onSyncDone)
    }
  }, [socket])

  const { emailSync, contactProcessing, sentimentAnalysis } = stats

  // Email Sync — per-user from SyncJob DB; syncLive only drives per-job progress bars
  const syncLiveList = Object.values(syncLive)
  const isSyncLive = syncLiveList.length > 0
  const syncActiveJobs = isSyncLive
    ? syncLiveList.map((j) => ({ ...j, accountEmail: j.accountEmail || '' }))
    : emailSync.activeJobs
  const syncActiveCount = Math.max(emailSync.active, syncLiveList.length)

  // Contact processing — clamp to avoid negative pending when poll/socket disagree briefly
  const contactTotal = contactProcessing.totalEmails
  const contactRemaining = contactLive
    ? Math.min(Math.max(0, contactLive.totalRemaining ?? 0), contactTotal)
    : contactProcessing.unprocessedEmails
  const contactProcessed = Math.max(0, contactTotal - contactRemaining)
  const contactPending = Math.max(0, contactTotal - contactProcessed)
  const contactPct = pct(contactProcessed, contactTotal)

  // Sentiment analysis — same pattern
  const sentimentTotal = sentimentAnalysis.totalContacts
  const sentimentRemaining = sentimentLive
    ? Math.min(Math.max(0, sentimentLive.totalRemaining ?? 0), sentimentTotal)
    : sentimentAnalysis.unanalyzedContacts
  const sentimentAnalyzed = Math.max(0, sentimentTotal - sentimentRemaining)
  const sentimentPending = Math.max(0, sentimentTotal - sentimentAnalyzed)
  const sentimentPct = pct(sentimentAnalyzed, sentimentTotal)

  return (
    <div className="live-jobs-grid">

      {/* Email Sync card */}
      <div className="live-job-card">
        <div className="live-job-card-header">
          <span className="live-job-title">Email Sync</span>
          <span className={statusDotClass(isSyncLive || syncActiveCount > 0)} />
        </div>
        {syncActiveJobs.length > 0 && (
          <div className="live-job-card-toolbar">
            <button
              type="button"
              className="live-job-cancel-btn live-job-cancel-btn-all"
              onClick={() => syncActiveJobs.forEach((j) => cancelSyncJob(j.jobId))}
              disabled={syncActiveJobs.every((j) => cancellingSync[j.jobId])}
            >
              Cancel all
            </button>
          </div>
        )}
        <div className="live-job-metrics">
          <div className="live-job-metric">
            <span className="live-job-metric-label">Active</span>
            <span className="live-job-metric-value">{syncActiveCount}</span>
          </div>
          <div className="live-job-metric">
            <span className="live-job-metric-label">Completed</span>
            <span className="live-job-metric-value">{emailSync.completed}</span>
          </div>
          <div className="live-job-metric">
            <span className="live-job-metric-label">Failed</span>
            <span className={`live-job-metric-value${emailSync.failed > 0 ? ' live-job-metric-failed' : ''}`}>{emailSync.failed}</span>
          </div>
        </div>
        {syncActiveJobs.length > 0 && (
          <div className="live-job-active-list">
            {syncActiveJobs.map((j) => {
              const total = j.totalEmails || 0
              const fetched = j.fetchedEmails || 0
              const inserted = j.insertedEmails || 0
              const p = total > 0 ? pct(fetched, total) : 0
              const isCancelling = !!cancellingSync[j.jobId]
              return (
                <div key={j.jobId} className="live-job-active-item">
                  <div className="live-job-active-row">
                    <div className="live-job-active-label">{j.accountEmail || 'syncing…'}</div>
                    <button
                      type="button"
                      className="live-job-cancel-btn"
                      onClick={() => cancelSyncJob(j.jobId)}
                      disabled={isCancelling}
                      title="Cancel this sync"
                    >
                      {isCancelling ? 'Cancelling…' : 'Cancel'}
                    </button>
                  </div>
                  <div className="live-job-progress-wrap">
                    <div className="live-job-progress-bar">
                      <div className="live-job-progress-fill" style={{ width: `${p}%` }} />
                    </div>
                    <span className="live-job-progress-pct">{p}%</span>
                  </div>
                  <div className="live-job-active-counts">
                    {fetched.toLocaleString()} / {total.toLocaleString()} fetched · {inserted.toLocaleString()} inserted
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Contact Processing card */}
      <div className="live-job-card">
        <div className="live-job-card-header">
          <span className="live-job-title">Contact Processing</span>
          <span className={statusDotClass(!!contactLive)} />
        </div>
        {contactLive && (
          <div className="live-job-card-toolbar">
            <button
              type="button"
              className="live-job-cancel-btn live-job-cancel-btn-all"
              onClick={cancelContact}
              disabled={cancellingContact}
            >
              {cancellingContact ? 'Cancelling…' : 'Cancel'}
            </button>
          </div>
        )}
        <div className="live-job-metrics">
          <div className="live-job-metric">
            <span className="live-job-metric-label">Total emails</span>
            <span className="live-job-metric-value">{contactTotal.toLocaleString()}</span>
          </div>
          <div className="live-job-metric">
            <span className="live-job-metric-label">Processed</span>
            <span className="live-job-metric-value">{contactProcessed.toLocaleString()}</span>
          </div>
          <div className="live-job-metric">
            <span className="live-job-metric-label">Pending</span>
            <span className="live-job-metric-value">{contactPending.toLocaleString()}</span>
          </div>
        </div>
        <div className="live-job-progress-wrap">
          <div className="live-job-progress-bar">
            <div className="live-job-progress-fill" style={{ width: `${contactPct}%` }} />
          </div>
          <span className="live-job-progress-pct">{contactPct}%</span>
        </div>
        {contactLive && (
          <div className="live-job-batch-info">
            Last batch: +{contactLive.lastBatch?.batchSaved ?? 0} contacts saved
          </div>
        )}
      </div>

      {/* Sentiment Analysis card */}
      <div className="live-job-card">
        <div className="live-job-card-header">
          <span className="live-job-title">Sentiment Analysis</span>
          <span className={statusDotClass(!!sentimentLive)} />
        </div>
        {sentimentLive && (
          <div className="live-job-card-toolbar">
            <button
              type="button"
              className="live-job-cancel-btn live-job-cancel-btn-all"
              onClick={cancelSentiment}
              disabled={cancellingSentiment}
            >
              {cancellingSentiment ? 'Cancelling…' : 'Cancel'}
            </button>
          </div>
        )}
        <div className="live-job-metrics">
          <div className="live-job-metric">
            <span className="live-job-metric-label">Total contacts</span>
            <span className="live-job-metric-value">{sentimentTotal.toLocaleString()}</span>
          </div>
          <div className="live-job-metric">
            <span className="live-job-metric-label">Analysed</span>
            <span className="live-job-metric-value">{sentimentAnalyzed.toLocaleString()}</span>
          </div>
          <div className="live-job-metric">
            <span className="live-job-metric-label">Pending</span>
            <span className="live-job-metric-value">{sentimentPending.toLocaleString()}</span>
          </div>
        </div>
        <div className="live-job-progress-wrap">
          <div className="live-job-progress-bar">
            <div className="live-job-progress-fill" style={{ width: `${sentimentPct}%` }} />
          </div>
          <span className="live-job-progress-pct">{sentimentPct}%</span>
        </div>
        {sentimentLive && (
          <div className="live-job-batch-info">
            Last batch: +{sentimentLive.lastBatch?.batchAnalyzed ?? 0} analysed
          </div>
        )}
      </div>

    </div>
  )
}
